// File: `.../RejectJoinRequestDialog.tsx`
import React, { useState } from "react";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { Localizer } from "@/main/react/Types";
import { UserPlus, UserX } from "lucide-react";
import SimpleDialog, { openSimpleDialog, SimpleDialogFooter } from "@/main/react/Dialog/SimpleDialog";
import logger from "@/lib/logger";
import { Textarea } from "@/components/ui/textarea";
import { DESTRUCTIVE_NOTICE_SURFACE_CLASS } from "@/main/react/component/destructiveNoticeSurface";
import { localizeWithMemberName } from "@/main/react/component/localizeWithMemberName";

type RejectJoinRequestDialogProps = {
    title?: string;
    message?: React.ReactNode;
    yesLabel?: string;
    noLabel?: string;
    onConfirm?: (resolutionNote?: string) => void | Promise<void>;
    onClose?: () => void;
    localizer: Localizer;
    username: string;
    /**
     * What the inline banner says when `onConfirm` throws.
     *
     * <p>Defaults to `groups.request.reject.failed`, which is where it was HARDCODED until 2026-09-09.
     * That default is correct for the join-request callers and wrong for every other one: a failed
     * add-to-group rejection told a post's author that their *join request* could not be rejected, on a
     * surface where no join request exists. Pass this whenever the caller is not rejecting a join
     * request.
     */
    failureMessage?: string;
};

export function RejectJoinRequestDialogInner({
                                         message,
                                         yesLabel,
                                         noLabel,
                                         onConfirm,
                                         onClose,
                                         localizer,
                                         username,
                                         failureMessage,
                                     }: RejectJoinRequestDialogProps) {
    const [busy, setBusy] = useState(false);
    const [resolutionNote, setResolutionNote] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const resolvedYesLabel = yesLabel ?? localizer("groups.request.reject.yes");
    const resolvedNoLabel = noLabel ?? localizer("groups.request.reject.no");

    async function handleConfirm() {
        if (busy) return;
        setErrorMessage(null);
        setBusy(true);
        try {
            await onConfirm?.(resolutionNote || undefined);
            onClose?.();
        } catch (e) {
            // eslint-disable-next-line no-console
            logger.error("Reject action failed", e);
            setErrorMessage(failureMessage ?? localizer("groups.request.reject.failed"));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="w-full">
            {message ?? <div className="mb-2">{localizeWithMemberName(localizer, "groups.request.reject.message", username)}</div>}
            {errorMessage ? (
                <div
                    role="alert"
                    aria-live="assertive"
                    className={`mb-3 rounded-2xl px-4 py-3 text-sm text-muted-foreground ${DESTRUCTIVE_NOTICE_SURFACE_CLASS}`}
                >
                    {errorMessage}
                </div>
            ) : null}
            <Textarea
                value={resolutionNote}
                onChange={(e) => {
                    setResolutionNote(e.target.value);
                    if (errorMessage) {
                        setErrorMessage(null);
                    }
                }}
                placeholder={localizer("groups.request.reject.placeholder")}
                className="min-h-24"
            />
            <SimpleDialogFooter>
                <CtaButton
                    type="button"
                    onClick={() => onClose?.()}
                    icon={UserX}
                    text={resolvedNoLabel}
                    variant="outline"
                    disabled={busy}
                />
                <CtaButton
                    type="button"
                    onClick={handleConfirm}
                    icon={UserPlus}
                    text={resolvedYesLabel}
                    variant="destructive"
                    disabled={busy}
                />
            </SimpleDialogFooter>
        </div>
    );
}

export function showRejectJoinRequestDialog(options: {
    localizer: Localizer;
    username: string;
    title?: string;
    message?: React.ReactNode;
    yesLabel?: string;
    noLabel?: string;
    onConfirm?: (resolutionNote?: string) => void | Promise<void>;
    failureMessage?: string;
}) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<div id="reject-request-modal-title">{options.title ?? (options.localizer("groups.request.reject.title"))}</div>}
            width="md"
        >
            <RejectJoinRequestDialogInner
                {...options}
                onConfirm={options.onConfirm}
                onClose={() => onOpenChange(false)}
            />
        </SimpleDialog>
    ));
}
